import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@web/lib/trpc';

import { toast } from 'sonner';

import { sameItems } from '@shared/utils';
import { cn } from '@web/lib/shadcn';

import { Save, Undo } from 'lucide-react';

import { Button } from '@web/components/ui/button';
import { Combobox } from '@web/components/ui/combobox';

export function ContactProcedures({ contactId }: { contactId: string }) {
  const { mutateAsync: setProceduresAssociatedToContact } =
    trpc.procedure.setProceduresAssociatedToContact.useMutation();

  const { data: allProceduresData } = trpc.procedure.listAll.useQuery();

  const { data: contactProcedures, refetch: refetchContactProcedures } =
    trpc.procedure.listContactProcedures.useQuery({
      contactId,
    });

  const contactProcedureNames = useMemo(
    () => contactProcedures?.map((p) => p.displayName),
    [contactProcedures],
  );

  const [tmpContactProcedures, setTmpContactProcedures] = useState<string[]>(
    [],
  );

  const resetTmpContactProcedures = () => {
    if (contactProcedureNames) setTmpContactProcedures(contactProcedureNames);
  };

  const handleUpdateProceduresAssociatedToContact = async (
    procedures: string[],
  ) => {
    const procedureIds = procedures
      .map(
        (procedureName) =>
          allProceduresData?.find((p) => p.displayName === procedureName)?.id,
      )
      .filter((id) => id !== undefined);

    await setProceduresAssociatedToContact({
      contactId,
      procedureIds,
      relationType: 'selected',
    });

    await refetchContactProcedures();

    toast.success('Associtated procedures updated');
  };

  useEffect(() => {
    if (contactProcedureNames && contactProcedureNames.length > 0)
      setTmpContactProcedures((tmp) =>
        tmp.length === 0 ? contactProcedureNames : tmp,
      );
  }, [contactProcedureNames]);

  return (
    <div>
      <div className="mb-2 ml-2 flex items-center justify-between gap-4">
        <h3 className="my-2">Procedures</h3>

        <span
          className={cn(
            'flex items-center gap-0',
            !contactProcedureNames ||
              (sameItems(tmpContactProcedures, contactProcedureNames) &&
                'hidden'),
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void handleUpdateProceduresAssociatedToContact(
                tmpContactProcedures,
              );
            }}
          >
            <Save className="size-4 stroke-green-700" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              resetTmpContactProcedures();
            }}
          >
            <Undo className="stroke-destructive size-4" />
          </Button>
        </span>
      </div>

      <Combobox
        items={allProceduresData?.map((p) => p.displayName) ?? []}
        stateManager={[tmpContactProcedures, setTmpContactProcedures]}
        className="w-full"
      />
    </div>
  );
}
